import { Link } from "wouter";
import { Seo } from "@/components/Seo";
import { Section, PageHeader, Eyebrow } from "@/components/Section";
import { EncryptedShieldPanel } from "@/components/EncryptedShieldPanel";
import { AnimatedNumber } from "@/components/AnimatedNumber";
import { CyberCrimeStats } from "@/components/CyberCrimeStats";
import { AustraliaCallout } from "@/components/AustraliaCallout";
import { Lock, KeyRound, Database, FileSearch, Globe2, ShieldCheck, EyeOff, Archive } from "lucide-react";

const POSTURE = [
  {
    icon: Lock,
    title: "Bank-grade encryption, end to end",
    body: "Every detail your client sends — bank accounts, contact info, ID numbers, addresses — is encrypted the moment it leaves their device and stays encrypted while we hold it. The same standard banks and government services use to protect their own data.",
  },
  {
    icon: ShieldCheck,
    title: "Verified or nothing",
    body: "If our identity check can't confirm who someone is, the request stops. Your client can't submit. Your staff get told. There's no quiet fallback, no \"close enough\" — a request marked verified means the person on the other end is exactly who they said they are.",
  },
  {
    icon: KeyRound,
    title: "Your workspace, your keys",
    body: "Every workspace has its own private setup. Nothing about your clients, your branding, or your verification history is visible to any other workspace on the platform — full stop.",
  },
  {
    icon: Database,
    title: "Strict separation between workspaces",
    body: "Your data lives in its own protected lane. The platform is built so that one workspace's information can never accidentally appear in another's account, and every part of the system is checked against that rule before code ships.",
  },
  {
    icon: Archive,
    title: "7 years of evidence, on hand",
    body: "Every verification, every change, every approval is kept for seven years so you have the proof you need for trust-account audits and Privacy Act obligations. Admins can download the full record any time.",
  },
  {
    icon: EyeOff,
    title: "We don't keep what we don't need",
    body: "Client phone numbers, bank details, addresses and ID numbers are never written to our error logs or analytics — they're filtered out before anything leaves the platform. The verification page itself doesn't record screen activity. Your clients' sensitive moments stay private.",
  },
  {
    icon: FileSearch,
    title: "Every action is logged",
    body: "Sign-ins, request approvals, branding updates, credential changes — anything that matters is written to a tamper-evident audit log tied to the person and the workspace. You always know who did what and when.",
  },
  {
    icon: Globe2,
    title: "Hosted in Australia",
    body: "Client data — identities, bank details, addresses, audit history — is stored on Australian-based servers in the Sydney region. Backups are encrypted and kept in the same region. Transactional notifications are delivered via sub-processors who receive only the minimum contact details required, under APP-aligned contractual safeguards.",
  },
];

export default function Security() {
  return (
    <>
      <Seo
        title="Security"
        description="How FortifyID protects your business and your clients: bank-grade encryption end to end, verified-or-nothing identity checks, strict workspace-by-workspace separation, seven years of evidence, and Australian hosting."
        path="/security"
      />
      <Section>
        <PageHeader
          eyebrow="Security"
          title="Security is the product."
          lede="FortifyID's reason to exist is reducing fraud risk on outbound payments and sensitive client details — across real estate trust accounts, legal disbursements, payroll runs, refunds and claim payouts. The platform itself is built to the same standard."
        />
      </Section>
      <Section className="!pt-0">
        <div
          className="rounded-2xl border border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))] p-6 sm:p-8 grid sm:grid-cols-[auto,1fr] gap-6 items-center"
          data-testid="callout-security-stat"
        >
          <AnimatedNumber
            value="$166.8M"
            className="block text-4xl sm:text-5xl font-semibold text-[hsl(var(--brand-forest))] leading-none"
          />
          <div className="text-sm text-[hsl(var(--brand-charcoal))] leading-relaxed">
            Australians reported losses of over <strong>$166.8 million</strong> to payment redirection scams in 2025, alongside <strong>84,861</strong> phishing and email scam reports. Trust-account inflows, vendor settlement payments and rental disbursements are the exact transfers these attacks target. The controls below are designed to remove email as an attack surface for those changes.
            <div className="mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--brand-slate))]">
              Source: ACCC Targeting Scams report, 2025
            </div>
          </div>
        </div>
      </Section>
      <CyberCrimeStats />
      <Section className="!pt-0">
        <AustraliaCallout />
      </Section>
      <Section className="!pt-0">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Eyebrow>How your data is protected</Eyebrow>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold leading-tight text-[hsl(var(--brand-charcoal))]">
              Sensitive details are locked away the moment they arrive.
            </h2>
            <p className="mt-5 text-[hsl(var(--brand-slate))] leading-relaxed">
              When a client types in a bank account, contact number, address or ID number, it's protected from their first keystroke. The information travels to us over the same encrypted connection your bank uses, and the second it lands it's sealed away with strong encryption — so even our own staff and infrastructure can't read it casually sitting on disk.
            </p>
            <p className="mt-4 text-[hsl(var(--brand-slate))] leading-relaxed">
              Once those details are submitted, the platform never hands them back out in a form anyone could screenshot or forward. Your team sees what they need to act on — verified status, name match, the details for the change they're processing — and nothing more.
            </p>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <EncryptedShieldPanel />
          </div>
        </div>
      </Section>
      <Section className="!pt-0">
        <div className="grid md:grid-cols-2 gap-px bg-[hsl(var(--brand-sand))] border border-[hsl(var(--brand-sand))] rounded-xl overflow-hidden">
          {POSTURE.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="bg-[hsl(var(--brand-bone))] p-6 sm:p-8">
                <div className="w-10 h-10 rounded bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-brass))] flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <h2 className="mt-5 text-xl font-semibold text-[hsl(var(--brand-charcoal))]">{p.title}</h2>
                <p className="mt-2 text-[hsl(var(--brand-slate))] leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </Section>
      <Section className="!pt-0">
        <div className="rounded-2xl border border-[hsl(var(--brand-sand))] bg-white/50 p-6 sm:p-10">
          <Eyebrow>Compliance</Eyebrow>
          <div className="mt-4 grid md:grid-cols-3 gap-8 text-sm text-[hsl(var(--brand-slate))]">
            <div>
              <div className="font-semibold text-[hsl(var(--brand-charcoal))] mb-2">Australian Privacy Principles</div>
              <p>
                FortifyID is designed to support your obligations under the APPs and the Privacy Act 1988. Read our{" "}
                <Link href="/legal/privacy" className="underline hover:text-[hsl(var(--brand-forest))]">Privacy Policy</Link> and{" "}
                <Link href="/legal/dpa" className="underline hover:text-[hsl(var(--brand-forest))]">DPA</Link>.
              </p>
            </div>
            <div>
              <div className="font-semibold text-[hsl(var(--brand-charcoal))] mb-2">ConnectID</div>
              <p>
                Aligned with the ConnectID trust framework for Australian digital identity. See{" "}
                <Link href="/how-it-works" className="underline hover:text-[hsl(var(--brand-forest))]">how a request flows</Link>.
              </p>
            </div>
            <div>
              <div className="font-semibold text-[hsl(var(--brand-charcoal))] mb-2">Hosting</div>
              <p>
                Production data is hosted on Australian-based servers with encrypted, region-locked backups.
              </p>
            </div>
          </div>
          <div className="mt-10 border-t border-[hsl(var(--brand-sand))] pt-8" data-testid="section-au-sovereignty">
            <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--brand-slate))]">Australian data sovereignty</div>
            <h3 className="mt-3 text-2xl font-semibold text-[hsl(var(--brand-charcoal))]">Your client data is stored in Australia.</h3>
            <div className="mt-4 grid md:grid-cols-3 gap-8 text-sm text-[hsl(var(--brand-slate))] leading-relaxed">
              <p>
                <strong className="text-[hsl(var(--brand-charcoal))]">Where it lives.</strong> Production databases, file storage and the audit log all run on Australian-based servers in the Sydney region (AWS ap-southeast-2). Primary data is not replicated outside Australia.
              </p>
              <p>
                <strong className="text-[hsl(var(--brand-charcoal))]">Where it travels.</strong> Verification traffic terminates inside Australia. ConnectID calls go directly to AU-domiciled identity providers. Transactional email and SMS are delivered via sub-processors who receive only minimum contact details, under APP-aligned contractual safeguards.
              </p>
              <p>
                <strong className="text-[hsl(var(--brand-charcoal))]">Backups & disaster recovery.</strong> Backups are encrypted and region-locked to Australia. We do not restore or test-restore client data to infrastructure outside the country.
              </p>
            </div>
          </div>
          <div className="mt-8 border-t border-[hsl(var(--brand-sand))] pt-6 text-sm text-[hsl(var(--brand-slate))]">
            Found a vulnerability? Please report it through our{" "}
            <Link href="/contact?topic=security" className="underline hover:text-[hsl(var(--brand-forest))]">contact form</Link>{" "}
            and a security engineer will follow up.
          </div>
        </div>
      </Section>
    </>
  );
}
